import { useCallback, useEffect, useRef, useState } from 'react';

import type { AgentThreadMessage, AgentThreadSummary } from '@api/agent';
import { getAgentThreadMessages, getAgentThreads } from '@api/agent';

export type AgentChatMessage = {
    role: 'user' | 'assistant';
    content: string;
    /** Optional structured data from agent tools (e.g. replenishment plan). */
    data?: unknown;
};

type SendMessageFn<TResponse> = (payload: {
    query: string;
    threadId?: string;
}) => Promise<TResponse>;

type ParseResponseFn<TResponse> = (response: TResponse) => {
    threadId: string;
    content: string;
    data?: unknown;
};

/** SSE stream function that returns a ReadableStream */
type StreamMessageFn = (payload: {
    query: string;
    threadId?: string;
}) => Promise<ReadableStream<Uint8Array>>;

interface UseAgentChatConfig<TResponse> {
    /** Unique key for sessionStorage persistence (e.g. 'replenishment', 'alert-creation') */
    context: string;
    /** The API function to send a message (used when streamMessage is not provided) */
    sendMessage: SendMessageFn<TResponse>;
    /** Extract threadId and content from the API response */
    parseResponse: ParseResponseFn<TResponse>;
    /** Optional SSE stream function — when provided, enables real-time status updates */
    streamMessage?: StreamMessageFn;
}

interface UseAgentChatReturn {
    messages: AgentChatMessage[];
    input: string;
    setInput: (value: string) => void;
    loading: boolean;
    /** Status text from SSE status events (e.g. "Screening for understocked products...") */
    statusText: string | undefined;
    isRestoring: boolean;
    threadId: string | undefined;
    send: (text?: string) => Promise<void>;
    startNewChat: () => void;
    threads: AgentThreadSummary[];
    threadsLoading: boolean;
    switchThread: (threadId: string) => Promise<void>;
    refreshThreads: () => Promise<void>;
}

const STORAGE_PREFIX = 'agent_thread_';
const MESSAGES_PREFIX = 'agent_messages_';

/**
 * Get the current user's organizationId from localStorage to scope
 * agent session data per merchant.
 */
function getOrganizationId(): string | undefined {
    try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            return user.organizationId || undefined;
        }
    } catch {
        // Ignore parse errors
    }
    return undefined;
}

/**
 * Build an org-scoped storage key so agent threads don't leak across orgs.
 * Format: {prefix}{context}_{orgId}
 */
function buildStorageKey(prefix: string, context: string): string {
    const orgId = getOrganizationId();
    return orgId ? `${prefix}${context}_${orgId}` : `${prefix}${context}`;
}

/**
 * Clears all agent thread and message data from sessionStorage.
 * Call this on logout/login to prevent cross-org thread persistence.
 */
export function clearAllAgentSessionStorage(): void {
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (
                key &&
                (key.startsWith(STORAGE_PREFIX) ||
                    key.startsWith(MESSAGES_PREFIX))
            ) {
                keysToRemove.push(key);
            }
        }
        for (const key of keysToRemove) {
            sessionStorage.removeItem(key);
        }
    } catch {
        // sessionStorage unavailable
    }
}

function getStoredThreadId(context: string): string | undefined {
    try {
        return sessionStorage.getItem(buildStorageKey(STORAGE_PREFIX, context)) ?? undefined;
    } catch {
        return undefined;
    }
}

function setStoredThreadId(context: string, threadId: string): void {
    try {
        sessionStorage.setItem(buildStorageKey(STORAGE_PREFIX, context), threadId);
    } catch {
        // sessionStorage unavailable
    }
}

function clearStoredThreadId(context: string): void {
    try {
        sessionStorage.removeItem(buildStorageKey(STORAGE_PREFIX, context));
    } catch {
        // sessionStorage unavailable
    }
}

function getStoredMessages(context: string): AgentChatMessage[] {
    try {
        const raw = sessionStorage.getItem(buildStorageKey(MESSAGES_PREFIX, context));
        if (!raw) return [];
        return JSON.parse(raw) as AgentChatMessage[];
    } catch {
        return [];
    }
}

function setStoredMessages(context: string, messages: AgentChatMessage[]): void {
    try {
        sessionStorage.setItem(buildStorageKey(MESSAGES_PREFIX, context), JSON.stringify(messages));
    } catch {
        // sessionStorage unavailable
    }
}

function clearStoredMessages(context: string): void {
    try {
        sessionStorage.removeItem(buildStorageKey(MESSAGES_PREFIX, context));
    } catch {
        // sessionStorage unavailable
    }
}

export function useAgentChat<TResponse>({
    context,
    sendMessage,
    parseResponse,
    streamMessage,
}: UseAgentChatConfig<TResponse>): UseAgentChatReturn {
    const [messages, setMessages] = useState<AgentChatMessage[]>(() =>
        getStoredMessages(context),
    );
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [statusText, setStatusText] = useState<string | undefined>(undefined);
    const [isRestoring, setIsRestoring] = useState(false);
    const [threadId, setThreadId] = useState<string | undefined>(() =>
        getStoredThreadId(context),
    );
    const [threads, setThreads] = useState<AgentThreadSummary[]>([]);
    const [threadsLoading, setThreadsLoading] = useState(false);
    const restoredRef = useRef(false);

    // Persist messages to sessionStorage whenever they change
    useEffect(() => {
        if (messages.length > 0) {
            setStoredMessages(context, messages);
        }
    }, [context, messages]);

    // On mount: if we have a cached threadId but no cached messages,
    // try to restore from the backend
    useEffect(() => {
        if (restoredRef.current) return;
        restoredRef.current = true;

        const storedThreadId = getStoredThreadId(context);
        const cachedMessages = getStoredMessages(context);

        // If we have cached messages, they're already loaded via useState initializer
        if (cachedMessages.length > 0 || !storedThreadId) return;

        // No cached messages but we have a threadId — try backend restore
        setIsRestoring(true);
        getAgentThreadMessages(storedThreadId)
            .then((response) => {
                if (response.success && response.messages.length > 0) {
                    const restored = response.messages.map(
                        (m: AgentThreadMessage) => ({
                            role: m.role,
                            content: m.content,
                        }),
                    );
                    setMessages(restored);
                    setThreadId(storedThreadId);
                } else {
                    clearStoredThreadId(context);
                    clearStoredMessages(context);
                    setThreadId(undefined);
                }
            })
            .catch(() => {
                // Backend unavailable — clear threadId since we can't restore
                clearStoredThreadId(context);
                clearStoredMessages(context);
                setThreadId(undefined);
            })
            .finally(() => {
                setIsRestoring(false);
            });
    }, [context]);

    const refreshThreads = useCallback(async () => {
        setThreadsLoading(true);
        try {
            const response = await getAgentThreads(context);
            if (response.success) {
                setThreads(response.threads);
            }
        } catch {
            // silently fail — threads panel just stays empty
        } finally {
            setThreadsLoading(false);
        }
    }, [context]);

    // Load thread list on mount
    useEffect(() => {
        void refreshThreads();
    }, [refreshThreads]);

    /**
     * Parse an SSE stream, calling handlers for each event type.
     */
    const parseSSEStream = useCallback(
        async (
            readableStream: ReadableStream<Uint8Array>,
            handlers: {
                onThread: (threadId: string) => void;
                onStatus: (content: string) => void;
                onResult: (data: TResponse) => void;
                onError: (message: string) => void;
            },
        ) => {
            const reader = readableStream.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let done = false;

            try {
                while (!done) {
                    const { value, done: readerDone } = await reader.read();
                    done = readerDone;
                    if (value) {
                        buffer += decoder.decode(value, { stream: true });
                        const lines = buffer.split('\n');
                        buffer = lines.pop() || '';

                        for (const line of lines) {
                            if (!line.startsWith('data: ')) continue;
                            const data = line.slice(6);
                            try {
                                const parsed = JSON.parse(data);
                                if (parsed.type === 'thread') {
                                    handlers.onThread(parsed.threadId);
                                } else if (parsed.type === 'status') {
                                    handlers.onStatus(parsed.content);
                                } else if (parsed.type === 'result') {
                                    handlers.onResult(parsed.data as TResponse);
                                } else if (parsed.type === 'error') {
                                    handlers.onError(parsed.message);
                                }
                            } catch {
                                // skip unparseable lines
                            }
                        }
                    }
                }
            } finally {
                reader.releaseLock();
            }
        },
        [],
    );

    const send = useCallback(
        async (text?: string) => {
            const query = (text ?? input).trim();
            if (!query || loading) return;

            setMessages((prev) => [...prev, { role: 'user', content: query }]);
            setInput('');
            setLoading(true);
            setStatusText(undefined);

            try {
                if (streamMessage) {
                    // SSE streaming path
                    const stream = await streamMessage({ query, threadId });
                    let receivedThreadId = threadId;

                    await parseSSEStream(stream, {
                        onThread: (newThreadId) => {
                            if (!threadId) {
                                receivedThreadId = newThreadId;
                                setThreadId(newThreadId);
                                setStoredThreadId(context, newThreadId);
                                void refreshThreads();
                            }
                        },
                        onStatus: (content) => {
                            setStatusText(content);
                        },
                        onResult: (responseData) => {
                            const parsed = parseResponse(responseData);
                            setMessages((prev) => [
                                ...prev,
                                { role: 'assistant', content: parsed.content, data: parsed.data },
                            ]);
                        },
                        onError: (message) => {
                            setMessages((prev) => [
                                ...prev,
                                { role: 'assistant', content: message },
                            ]);
                        },
                    });
                } else {
                    // Standard JSON request path
                    const response = await sendMessage({ query, threadId });
                    const parsed = parseResponse(response);

                    const isNewThread = !threadId && parsed.threadId;
                    if (isNewThread) {
                        setThreadId(parsed.threadId);
                        setStoredThreadId(context, parsed.threadId);
                    }

                    setMessages((prev) => [
                        ...prev,
                        { role: 'assistant', content: parsed.content, data: parsed.data },
                    ]);

                    if (isNewThread) {
                        void refreshThreads();
                    }
                }
            } catch {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'assistant',
                        content:
                            'Sorry, something went wrong. Please try again.',
                    },
                ]);
            } finally {
                setLoading(false);
                setStatusText(undefined);
            }
        },
        [input, loading, threadId, context, sendMessage, parseResponse, streamMessage, parseSSEStream, refreshThreads],
    );

    const switchThread = useCallback(
        async (targetThreadId: string) => {
            if (targetThreadId === threadId) return;

            setIsRestoring(true);
            try {
                const response = await getAgentThreadMessages(targetThreadId);
                if (response.success && response.messages.length > 0) {
                    const restored = response.messages.map(
                        (m: AgentThreadMessage) => ({
                            role: m.role,
                            content: m.content,
                        }),
                    );
                    setMessages(restored);
                    setThreadId(targetThreadId);
                    setStoredThreadId(context, targetThreadId);
                    setStoredMessages(context, restored);
                }
            } catch {
                // Failed to load thread — stay on current
            } finally {
                setIsRestoring(false);
            }
        },
        [threadId, context],
    );

    const startNewChat = useCallback(() => {
        clearStoredThreadId(context);
        clearStoredMessages(context);
        setMessages([]);
        setInput('');
        setThreadId(undefined);
        setLoading(false);
    }, [context]);

    return {
        messages,
        input,
        setInput,
        loading,
        statusText,
        isRestoring,
        threadId,
        send,
        startNewChat,
        threads,
        threadsLoading,
        switchThread,
        refreshThreads,
    };
}
